/**
 * Publish the live MapLibre camera into the :file:`map/map-viewport-store`.
 *
 * Extracted from :file:`Map.tsx` (the same testability rationale as
 * :file:`map/viewport-sync.ts`) so the defensive ``getCenter`` / ``getZoom``
 * read can be unit-tested without booting a real :class:`MapLibreMap`, and so
 * the Map's ``onMapReady`` + settled-``moveend`` paths share ONE camera-read
 * (CLAUDE.md §4.4 — no duplicated logic). This feeds the Profile editor's
 * "Set home to current view" capture (REQ-UI-076 / REQ-UI-090; ADR-033 §9.4).
 *
 * # Defensive read
 *
 * MapLibre's :meth:`Map.getCenter` returns a ``LngLat`` (``{ lng, lat }``);
 * we re-shape it to the Profile ``map.home`` ``{ lon, lat }`` form. The read
 * is guarded: a non-finite ``lng`` / ``lat`` / ``zoom`` (projection-edge
 * float math, or a map double in tests that doesn't implement these methods)
 * publishes nothing rather than poisoning the store with ``NaN`` — operator
 * "Set home" then stays disabled until a real camera arrives (CLAUDE.md §8.1
 * fail-safe). This also keeps the existing :file:`Map.test.tsx` fake map
 * (which has no ``getCenter`` / ``getZoom``) from throwing.
 *
 * Cross-references:
 *   - REQ-UI-076 (Profile editor — capture current view) / REQ-UI-090
 *     (nav-home → Profile home); ADR-033 §9.4.
 *   - map/map-viewport-store.ts (the sink).
 */
import { useMapViewportStore } from "@/map/map-viewport-store";

/**
 * The slice of the MapLibre :class:`Map` surface :func:`publishMapViewport`
 * reaches into. Both methods are optional so a partial test double (or a map
 * that hasn't finished constructing) is handled by the finite-guard rather
 * than a ``TypeError``.
 */
export interface CameraReadMapLike {
  getCenter?: () => { lng: number; lat: number };
  getZoom?: () => number;
}

/**
 * Read ``map``'s current camera and publish it to the viewport store. No-op
 * (publishes nothing) when the camera can't be read as finite numbers — so a
 * map that isn't ready, or a test double without the getters, never writes a
 * ``NaN`` home (CLAUDE.md §8.1).
 *
 * @param map - The loaded MapLibre map (or a camera-read double).
 */
export function publishMapViewport(map: CameraReadMapLike): void {
  const center = map.getCenter?.();
  const zoom = map.getZoom?.();
  if (
    center === undefined ||
    zoom === undefined ||
    !Number.isFinite(center.lng) ||
    !Number.isFinite(center.lat) ||
    !Number.isFinite(zoom)
  ) {
    return;
  }
  useMapViewportStore.getState().setViewport({
    center: { lon: center.lng, lat: center.lat },
    zoom,
  });
}
